import { useEffect } from "react";
import { useBranding } from "../lib/branding";

// Platform-wide script injector — mounts once at App root and appends any
// Superadmin-configured head/body-start/body-end snippets into the live DOM.
// Snippets are inserted verbatim (superadmin-owned config, not user input),
// so raw <script>, <noscript>, <meta>, or `<style>` tags all work — matching
// how Google Tag Manager, Intercom, Crisp, Hotjar etc. tell customers to paste
// their code.
//
// Idempotent: injected nodes are tagged with data-pragma-injected so we never
// duplicate them across hot-reloads or branding refetches.

const MARKER = "data-pragma-injected";

function clearInjected() {
  document.querySelectorAll(`[${MARKER}]`).forEach((el) => el.remove());
}

function inject(html, target, position) {
  if (!html || !html.trim()) return;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  // Move each node individually so <script> tags execute when appended.
  Array.from(wrapper.childNodes).forEach((node) => {
    if (node.nodeType === 1 /* Element */) {
      // Re-create <script> tags so they actually execute (innerHTML script tags don't).
      if (node.tagName === "SCRIPT") {
        const s = document.createElement("script");
        for (const attr of node.attributes) s.setAttribute(attr.name, attr.value);
        s.text = node.textContent || "";
        s.setAttribute(MARKER, "true");
        insertAt(target, position, s);
      } else {
        node.setAttribute(MARKER, "true");
        insertAt(target, position, node);
      }
    } else if (node.nodeType === 3 /* Text */ && node.textContent.trim()) {
      // Wrap loose text (rare) in a marked comment so we can clean it up.
      const c = document.createComment(` pragma-injected: ${node.textContent.slice(0, 40)} `);
      insertAt(target, position, c);
    }
  });
}

function insertAt(target, position, node) {
  if (position === "prepend" && target.firstChild) target.insertBefore(node, target.firstChild);
  else target.appendChild(node);
}

export default function BrandingInjector() {
  const branding = useBranding();

  useEffect(() => {
    if (!branding) return;
    clearInjected();
    inject(branding.head_html,       document.head, "append");
    inject(branding.body_start_html, document.body, "prepend");
    inject(branding.body_end_html,   document.body, "append");
  }, [branding?.head_html, branding?.body_start_html, branding?.body_end_html]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
